import { useCallback, useEffect, useRef } from 'react';

import type { SanityQuizBlock } from '@lib/sanity/types';

type HashSlugBlock = SanityQuizBlock & { hashSlug?: string };

const getHashFromUrl = (): string => {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash;
  return hash.startsWith('#') ? hash.slice(1) : '';
};

const findBlockIndexByHash = (
  blocks: HashSlugBlock[],
  hash: string
): number => {
  if (!hash) return -1;
  return blocks.findIndex(
    (block) => 'hashSlug' in block && block.hashSlug === hash
  );
};

/**
 * Syncs the URL hash fragment with the current quiz step for analytics tracking.
 *
 * - Updates `window.location.hash` when `currentStepIndex` changes
 * - Supports browser back/forward navigation via `popstate`
 * - Accepts a hash override (used by GytrBlock for internal sub-steps)
 * - On mount, reads the hash to restore the correct step
 */
const useQuizHashTracking = (
  blocks: HashSlugBlock[],
  currentStepIndex: number,
  onStepChange: (index: number) => void
) => {
  const isNavigatingRef = useRef(false);
  const hashOverrideRef = useRef<string | null>(null);

  // Push hash to URL without triggering a popstate event
  const pushHash = useCallback((slug: string | undefined) => {
    if (typeof window === 'undefined') return;

    const newHash = slug ? `#${slug}` : '';
    const currentHash = window.location.hash || '';

    if (newHash === currentHash) return;

    isNavigatingRef.current = true;

    if (newHash) {
      window.history.pushState(null, '', newHash);
    } else {
      // Clear the hash by pushing the path without a fragment
      window.history.pushState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }

    isNavigatingRef.current = false;
  }, []);

  // Update hash when the active block changes
  useEffect(() => {
    // If there's a GYTR internal override active, let it control the hash
    if (hashOverrideRef.current !== null) return;

    const block = blocks[currentStepIndex] as HashSlugBlock | undefined;
    pushHash(block?.hashSlug);
  }, [blocks, currentStepIndex, pushHash]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      if (isNavigatingRef.current) return;

      const hash = getHashFromUrl();

      // First check if a GYTR override matches (handled by GytrBlock's own listener)
      // Otherwise, check block-level hashes
      if (!hash) {
        onStepChange(0);
        return;
      }

      const blockIndex = findBlockIndexByHash(blocks, hash);
      if (blockIndex !== -1) {
        hashOverrideRef.current = null;
        onStepChange(blockIndex);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [blocks, onStepChange]);

  // On mount, check if the URL already has a hash and restore that step
  useEffect(() => {
    const hash = getHashFromUrl();
    if (!hash) return;

    const blockIndex = findBlockIndexByHash(blocks, hash);
    if (blockIndex !== -1 && blockIndex !== currentStepIndex) {
      onStepChange(blockIndex);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Override the URL hash with a custom slug (used by GytrBlock for internal steps).
   * Pass `null` to clear the override and let the block-level hash take over again.
   */
  const setHashOverride = useCallback(
    (slug: string | null) => {
      hashOverrideRef.current = slug;
      if (slug !== null) {
        pushHash(slug);
      } else {
        // Restore the current block's hash
        const block = blocks[currentStepIndex] as HashSlugBlock | undefined;
        pushHash(block?.hashSlug);
      }
    },
    [blocks, currentStepIndex, pushHash]
  );

  return { setHashOverride };
};

export default useQuizHashTracking;
